import { cn } from '@/lib/utils';
import type { TaskStatus, Priority } from '@/types';

interface BadgeProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  size?: 'sm' | 'md';
}

export function Badge({ children, className, variant = 'default', size = 'md' }: BadgeProps) {
  const variants = {
    default: 'bg-blue-100 text-blue-800 border-blue-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-purple-100 text-purple-800 border-purple-200',
    neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium border rounded-full',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </span>
  );
}

export function TaskStatusBadge({ status }: { status: TaskStatus }) {
  const statusConfig = {
    queued: { variant: 'neutral' as const, label: 'Queued', icon: '⏳' },
    'in-progress': { variant: 'info' as const, label: 'In Progress', icon: '🔄' },
    review: { variant: 'warning' as const, label: 'In Review', icon: '👀' },
    delivered: { variant: 'success' as const, label: 'Delivered', icon: '✅' },
    approved: { variant: 'success' as const, label: 'Approved', icon: '🎉' },
  };

  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} size="sm">
      <span className="mr-1">{config.icon}</span>
      {config.label}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: Priority }) {
  const config = {
    standard: { variant: 'neutral' as const, label: 'Standard' },
    urgent: { variant: 'danger' as const, label: 'Urgent' },
  };

  const { variant, label } = config[priority];

  return (
    <Badge variant={variant} size="sm">
      {priority === 'urgent' && '🔥 '}
      {label}
    </Badge>
  );
}
